import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { fetchIndexLatestSnapshot } from '../../api';
import { IndexLatestQuote } from '../../types';
import { AnalyticsDataContext } from '../../context/AnalyticsDataContext';

/**
 * NSE indices surfaced as a compact snapshot in the top bar. Add more entries here to
 * show additional indices - matched by IndexName (case-insensitive) against the compact
 * indices-latest.json snapshot (see fetchIndexLatestSnapshot in api.ts). This intentionally
 * avoids fetchIndexHistory(), which downloads each index's full multi-hundred-day history
 * just to read the latest entry.
 */
const TRACKED_INDICES = [
  { label: 'NIFTY 50', indexName: 'Nifty 50' },
  { label: 'NIFTY 500', indexName: 'Nifty 500' },
] as const;

interface IndexTickerItem {
  label: string;
  entry: IndexLatestQuote;
}

const priceFormatter = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const peFormatter = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const IndexTicker: React.FC = () => {
  const navigate = useNavigate();
  const analyticsContext = useContext(AnalyticsDataContext);
  const refreshing = analyticsContext?.refreshing ?? false;
  const [items, setItems] = useState<IndexTickerItem[]>([]);

  const loadSnapshot = useCallback(async (forceRefresh = false) => {
    try {
      const snapshot = await fetchIndexLatestSnapshot(forceRefresh ? { forceRefresh: true } : undefined);
      const quoteByName = new Map(snapshot.map((quote) => [quote.IndexName.toLowerCase(), quote]));

      const matched = TRACKED_INDICES
        .map(({ label, indexName }): IndexTickerItem | null => {
          const entry = quoteByName.get(indexName.toLowerCase());
          return entry ? { label, entry } : null;
        })
        .filter((item): item is IndexTickerItem => item !== null);

      setItems(matched);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void loadSnapshot(false);
  }, [loadSnapshot]);

  useEffect(() => {
    if (refreshing) {
      void loadSnapshot(true);
    }
  }, [refreshing, loadSnapshot]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className="ns-index-ticker"
      role="button"
      tabIndex={0}
      onClick={() => navigate('/index-heatmap')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          navigate('/index-heatmap');
        }
      }}
      title="Click to explore 150 NSE Index Heatmap Matrix"
      aria-label="Market index snapshot. Click to explore 150 NSE Index Heatmap Matrix."
      style={{ cursor: 'pointer' }}
    >
      {items.map(({ label, entry }) => {
        const close = entry.Close ?? null;
        const percentChange = entry.PercentChange ?? null;
        const pe = entry.PE ?? null;
        const isPositive = (percentChange ?? 0) >= 0;

        return (
          <div key={label} className="ns-index-ticker__item">
            <span className="ns-index-ticker__label">{label}</span>
            <span className="ns-index-ticker__value">{close != null ? priceFormatter.format(close) : '—'}</span>
            {percentChange != null && (
              <span
                className={`ns-index-ticker__change ${isPositive ? 'ns-index-ticker__change--positive' : 'ns-index-ticker__change--negative'
                  }`}
              >
                {isPositive ? <TrendingUp size={11} aria-hidden="true" /> : <TrendingDown size={11} aria-hidden="true" />}
                <span>
                  {isPositive ? '+' : ''}
                  {percentChange.toFixed(2)}%
                </span>
              </span>
            )}
            {pe != null && (
              <span className="ns-index-ticker__pe" title="Price to Earnings ratio">
                PE {peFormatter.format(pe)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default IndexTicker;
